import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Container } from '@mui/material';

// 官方品牌素材（透過 webpack import 確保打包）
import lineLogo from '../../assets/brand-icons/line.png';
import facebookLogo from '../../assets/brand-icons/facebook.png';
import messengerLogo from '../../assets/brand-icons/messenger.svg';

const BrandImg = ({ src, alt }) => (
  <img src={src} alt={alt} style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover' }} />
);

const InstagramIcon = () => (
  <svg width="56" height="56" viewBox="0 0 32 32" fill="none">
    <defs>
      <linearGradient id="igGrad" x1="2" y1="30" x2="30" y2="2">
        <stop offset="0%" stopColor="#FFC107"/>
        <stop offset="35%" stopColor="#FF5722"/>
        <stop offset="65%" stopColor="#E1306C"/>
        <stop offset="100%" stopColor="#9C27B0"/>
      </linearGradient>
    </defs>
    <rect width="32" height="32" rx="8" fill="url(#igGrad)"/>
    <rect x="8" y="8" width="16" height="16" rx="4.5" stroke="#fff" strokeWidth="1.8" fill="none"/>
    <circle cx="16" cy="16" r="3.5" stroke="#fff" strokeWidth="1.8" fill="none"/>
    <circle cx="21" cy="11" r="1.2" fill="#fff"/>
  </svg>
);

const WebChatIcon = () => (
  <svg width="56" height="56" viewBox="0 0 32 32" fill="none">
    <rect width="32" height="32" rx="8" fill="#3664D1"/>
    <circle cx="16" cy="16" r="7.5" stroke="#fff" strokeWidth="1.5" fill="none" />
    <ellipse cx="16" cy="16" rx="3.2" ry="7.5" stroke="#fff" strokeWidth="1.2" fill="none" />
    <line x1="8.5" y1="16" x2="23.5" y2="16" stroke="#fff" strokeWidth="1.2" />
    <line x1="9.5" y1="12.5" x2="22.5" y2="12.5" stroke="#fff" strokeWidth="0.8" opacity="0.5" />
    <line x1="9.5" y1="19.5" x2="22.5" y2="19.5" stroke="#fff" strokeWidth="0.8" opacity="0.5" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="56" height="56" viewBox="0 0 32 32" fill="none">
    <rect width="32" height="32" rx="8" fill="#FF6B35"/>
    <path d="M21.97 19.33l-2.35-1a.96.96 0 00-.96.13l-1.24 1a8.27 8.27 0 01-4.88-4.88l1-.95a.96.96 0 00.22-1.02l-1-2.35A1 1 0 0011.8 9.5h-1.3A1.5 1.5 0 009 11c0 6.08 4.92 11 11 11a1.5 1.5 0 001.5-1.5v-1.21a.96.96 0 00-.53-.96z" fill="#fff"/>
  </svg>
);

const PLATFORMS = [
  { key: 'line', attr: 'line', Icon: () => <BrandImg src={lineLogo} alt="LINE" /> },
  { key: 'fbPosts', attr: 'fb', Icon: () => <BrandImg src={facebookLogo} alt="Facebook" /> },
  { key: 'messenger', attr: 'msger', Icon: () => <BrandImg src={messengerLogo} alt="Messenger" /> },
  { key: 'instagram', attr: 'ig', Icon: InstagramIcon },
  { key: 'webChat', attr: 'web', Icon: WebChatIcon },
  { key: 'phone', attr: 'phone', Icon: PhoneIcon },
];

const PlatformGrid = () => {
  const { t } = useTranslation('landing');
  const sectionRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const reveals = section.querySelectorAll('.omni-reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    reveals.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <Box ref={sectionRef} component="section" sx={{ backgroundColor: '#fff', pt: '90px', pb: '60px' }}>
      <Container maxWidth="lg">
        <Typography className="omni-reveal" sx={{ textAlign: 'center', fontSize: '14px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: '#3664D1', mb: 1.5 }}>
          {t('platforms.label')}
        </Typography>
        <Typography className="omni-reveal" component="h2" sx={{ textAlign: 'center', fontSize: { xs: '28px', md: '36px' }, fontWeight: 800, color: '#0f1729', mb: 1.5 }}>
          {t('platforms.sectionTitle')}
        </Typography>
        <Typography className="omni-reveal" sx={{ textAlign: 'center', fontSize: '16px', color: '#6b7280', maxWidth: '560px', mx: 'auto', mb: 5 }}>
          {t('platforms.sectionSub')}
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' }, gap: '20px' }}>
          {PLATFORMS.map((platform, index) => (
            <Box key={platform.key} className={`omni-platform-card omni-reveal omni-reveal-delay-${(index % 3) + 1}`} data-platform={platform.attr}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                <platform.Icon />
              </Box>
              <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#0f1729', mb: 0.5 }}>
                {t(`platforms.${platform.key}.title`)}
              </Typography>
              <Typography sx={{ fontSize: '15px', color: '#4a5568', lineHeight: 1.6 }}>
                {t(`platforms.${platform.key}.desc`)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export { PlatformGrid };
